import React, { useMemo, useState } from 'react';
import type { CardComparativo, FaturaPainel } from '../types';
import { calcularComparativoCustomizado, METRICAS_COMPARADOR } from '../lib/calcularComparativoCustomizado';
import type { MetricaComparadorId, RangeMeses } from '../lib/calcularComparativoCustomizado';
import { mesReferenciaParaData } from '../../../../src/lib/financeiro/periodo';
import SeletorPeriodoCalendario from './SeletorPeriodoCalendario';
import GraficoDetalheMensal from './GraficoDetalheMensal';

interface Props {
  dados: FaturaPainel[];
  hoje: Date;
}

const MESES = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];
const fmtMes = (d: Date) => `${MESES[d.getMonth()]}/${d.getFullYear()}`;

const fmtBRL = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2 });
const fmtNum = (v: number) => v.toLocaleString('pt-BR');
const fmtPct = (v: number) => `${(v * 100).toFixed(1)}%`;
const fmtValor = (v: number, formato: CardComparativo['formato']) =>
  formato === 'percentual' ? fmtPct(v) : formato === 'numero' ? fmtNum(v) : fmtBRL(v);

// Mesmas cores usadas nas barras de GraficoDetalheMensal — repetidas aqui pra colorir o
// cabeçalho do card na mesma cor do grupo de barras que ele descreve.
const COR_PERIODO_A = '#5F6C37';
const COR_PERIODO_B = '#F6A937';

const METRICAS_PADRAO: MetricaComparadorId[] = ['faturamento', 'inadimplencia', 'boletosEmitidos'];

const ComparativoPeriodos: React.FC<Props> = ({ dados, hoje }) => {
  const mesAtual = fmtMes(hoje);
  const mesAnterior = fmtMes(new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1));

  const mesesDisponiveis = useMemo(() => {
    const set = new Set<string>([mesAtual, mesAnterior]);
    dados.forEach((f) => { if (f.mes_referencia) set.add(f.mes_referencia); });
    return Array.from(set).sort(
      (a, b) => (mesReferenciaParaData(a)?.getTime() ?? 0) - (mesReferenciaParaData(b)?.getTime() ?? 0)
    );
  }, [dados, mesAtual, mesAnterior]);

  const [periodoA, setPeriodoA] = useState<RangeMeses>({ inicio: mesAtual, fim: mesAtual });
  const [periodoB, setPeriodoB] = useState<RangeMeses>({ inicio: mesAnterior, fim: mesAnterior });
  const [metricas, setMetricas] = useState<MetricaComparadorId[]>(METRICAS_PADRAO);

  const toggleMetrica = (id: MetricaComparadorId) => {
    setMetricas((atual) => (atual.includes(id) ? atual.filter((m) => m !== id) : [...atual, id]));
  };

  const cards = useMemo(
    () => calcularComparativoCustomizado(dados, periodoA, periodoB, metricas),
    [dados, periodoA, periodoB, metricas]
  );

  return (
    <div>
      <h3 className="text-sm font-bold text-gray-800 dark:text-white mb-3">Comparativo de Períodos</h3>

      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-4 mb-4 space-y-3">
        <div className="flex flex-wrap items-start gap-4">
          <SeletorPeriodoCalendario label="De Período A" value={periodoA} onChange={setPeriodoA} mesesDisponiveis={mesesDisponiveis} />
          <SeletorPeriodoCalendario label="Para Período B" value={periodoB} onChange={setPeriodoB} mesesDisponiveis={mesesDisponiveis} />
        </div>
        <div className="flex flex-wrap gap-2 pt-1">
          {METRICAS_COMPARADOR.map((def) => {
            const ativo = metricas.includes(def.id);
            return (
              <button
                key={def.id}
                type="button"
                onClick={() => toggleMetrica(def.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${ativo
                  ? 'bg-primary/10 border-primary text-primary dark:bg-primary/20 dark:text-primary'
                  : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400'
                  }`}
              >
                {def.titulo}
              </button>
            );
          })}
        </div>
      </div>

      {cards.length === 0 ? (
        <p className="text-xs text-gray-400 italic">Selecione ao menos uma métrica para comparar.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
          {cards.map((c) => {
            const cor = c.corSemantica === 'positiva' ? 'text-emerald-600 dark:text-emerald-400' : c.corSemantica === 'negativa' ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400';
            return (
              <div key={c.titulo} className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-4">
                <p className="text-xs font-bold text-gray-800 dark:text-white">{c.titulo}</p>
                <p className="text-[11px] text-gray-400 mb-2">{c.metricaDescricao}</p>
                <p className="text-sm font-bold" style={{ color: COR_PERIODO_A }}>
                  <span className="font-normal text-gray-400">{c.rotuloAtual}:</span> {fmtValor(c.valorAtual, c.formato)}
                </p>
                <p className="text-[10px] font-semibold text-gray-400">vs</p>
                <p className="text-sm font-bold" style={{ color: COR_PERIODO_B }}>
                  <span className="font-normal text-gray-400">{c.rotuloAnterior}:</span> {fmtValor(c.valorAnterior, c.formato)}
                </p>
                <p className={`text-xs font-semibold mt-1 ${cor}`}>
                  {c.diferencaAbsoluta >= 0 ? '+' : ''}{fmtValor(c.diferencaAbsoluta, c.formato)}
                  {c.diferencaPercentual !== null && ` (${c.diferencaPercentual >= 0 ? '+' : ''}${c.diferencaPercentual.toFixed(1)}%)`}
                </p>
                {c.extra && <p className="text-[11px] text-gray-400 mt-1">{c.extra}</p>}
                {c.detalheMensal && (
                  <GraficoDetalheMensal
                    detalheMensal={c.detalheMensal}
                    formato={c.formato}
                    valorAtual={c.valorAtual}
                    valorAnterior={c.valorAnterior}
                    extraAtual={c.extraAtual}
                    extraAnterior={c.extraAnterior}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ComparativoPeriodos;
